<template id="add-source-modal-template">
    <base-form :do="save" class="flex flex-col" id="add-source-modal">
        <div class="modal-body content">
            <div v-if="genericError" class="form-error text-xs mb-4">{{ genericError }}</div>

            <select-input class="w-full mb-4" label="Ruimte" v-model="target" :options="targetOptions" name="target"
                v-validate="'required'" data-vv-as="Ruimte" :error="errors.first('target')" :has-permission="true"
                :required="true"></select-input>

            <div class="group fields border rounded p-4 mb-4" v-for="(row, index) in rows" :key="row.uid">
                <div class="flex justify-between items-center mb-2" v-if="rows.length > 1">
                    <span class="text-xs text-grey-dark uppercase tracking-wide">Bron {{ index + 1 }}</span>
                    <button type="button" class="btn btn-tertiary btn-sm" @click="removeRow(index)">Verwijder</button>
                </div>

                <text-input class="w-full" label="Bron" :name="`${index}.name`" type="text" v-model="row.name"
                    v-validate="'required|max:50'" data-vv-as="Bron" :error="errors.first(`${index}.name`)"
                    :has-permission="true" :required="true"></text-input>

                <div class="flex flex-wrap w-full mx-0">
                    <text-input class="w-full md:w-1/2" label="Oppervlakte" :name="`${index}.surface`" type="text"
                        v-model="row.surface" v-validate="'required|max:50'" data-vv-as="Oppervlakte"
                        :error="errors.first(`${index}.surface`)" :has-permission="true" :required="true"></text-input>
                    <div class="flex flex-wrap w-full md:w-1/2">
                        <text-input class="w-1/2 pr-2 md:px-2" label="Aantal" :name="`${index}.amount`" type="text"
                            v-model="row.amount" v-validate="'required|numeric|min_value:0'" data-vv-as="Aantal"
                            :error="errors.first(`${index}.amount`)" :has-permission="true" :required="true"></text-input>
                        <select-input class="w-1/2" label="Eenheid" v-model="row.unit" :options="units"
                            :name="`${index}.unit.id`" v-validate="'required'" data-vv-as="Eenheid"
                            :error="errors.first(`${index}.unit.id`)" :has-permission="true"
                            :required="true"></select-input>
                    </div>
                </div>

                <select-input class="w-full" label="Verwijdermethode" v-model="row.remove_method"
                    :options="removeMethods" :name="`${index}.remove_method.id`" v-validate="'required'"
                    data-vv-as="Verwijdermethode" :error="errors.first(`${index}.remove_method.id`)"
                    :has-permission="true" :required="true"></select-input>

                <select-input class="w-full" label="Status" v-model="row.status" :options="statuses"
                    :name="`${index}.status.id`" v-validate="'required'" data-vv-as="Status"
                    :error="errors.first(`${index}.status.id`)" :has-permission="true"
                    :required="true"></select-input>
            </div>

            <button type="button" class="btn btn-tertiary btn-sm" @click="addRow">+ Nog een bron toevoegen</button>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-tertiary btn-sm" @click="close">Cancel</button>
            <button type="submit" class="btn btn-primary btn-sm">Opslaan</button>
        </div>
    </base-form>
</template>

<script>
import SubjectService from '@/services/subject';
import SourceService from '@/services/source';
import BaseForm from '@/components/globals/BaseForm';
import TextInput from '@/components/inputs/Text';
import SelectInput from '@/components/inputs/Select';

import { EventBus } from '@/event-bus';
import { mapMutations } from 'vuex';
import { HIDE_MODAL } from '@/store/mutations.types';

export default {
    name: 'AddSourceModal',
    $_veeValidate: {
        validator: 'new'
    },
    components: {
        BaseForm,
        TextInput,
        SelectInput
    },
    data() {
        return {
            target: null,
            targetOptions: [],
            rows: [this.newRow(0)],
            nextUid: 1,
            error: null,
            genericError: null,
            units: [],
            removeMethods: [],
            statuses: []
        };
    },
    created() {
        const items = this.$store.state.modal.items;
        const rooms = (items.rooms || []).map(room => {
            return { id: room.id, name: room.name };
        });

        this.targetOptions = [
            { id: items.subjectId, name: 'Op object zelf' },
            ...rooms
        ];

        if (this.targetOptions.length === 1) {
            this.target = this.targetOptions[0];
        }

        this.fetchUnits();
        this.fetchRemoveMethods();
        this.fetchStatuses();
    },
    methods: {
        ...mapMutations('modal', {
            hide: HIDE_MODAL
        }),
        newRow(uid) {
            return {
                uid: uid,
                name: '',
                surface: '',
                amount: '',
                remove_method: null,
                unit: null,
                status: null
            };
        },
        addRow() {
            this.rows.push(this.newRow(this.nextUid++));
        },
        removeRow(index) {
            this.rows.splice(index, 1);
        },
        close() {
            this.$validator.reset();
            this.hide();
        },
        async fetchUnits() {
            try {
                const { data: response } = await SourceService.units();
                this.units = response.data;
            } catch (error) {
                this.units = null;
                this.error = error;
            }
        },
        async fetchRemoveMethods() {
            try {
                const { data: response } = await SourceService.removeMethods();
                this.removeMethods = response.data;
            } catch (error) {
                this.removeMethods = null;
                this.error = error;
            }
        },
        async fetchStatuses() {
            try {
                const { data: response } = await SourceService.statuses('source');
                this.statuses = response.data;
            } catch (error) {
                this.statuses = null;
                this.error = error;
            }
        },
        async beforeSend() {
            const result = await this.$validator.validateAll();
            if (result) this.save();
        },
        async save() {
            if (!this.target) return;
            this.genericError = null;

            const sources = this.rows.map(row => {
                return {
                    name: row.name,
                    surface: row.surface,
                    amount: row.amount,
                    remove_method: row.remove_method,
                    unit: row.unit,
                    status: row.status
                };
            });

            try {
                await SubjectService.addSources(this.target.id, sources);
                EventBus.$emit('sources-added');
                this.close();
            } catch (error) {
                if (error.data && error.data.details) {
                    this.addErrors(error.data);
                    return;
                }
                this.genericError = 'Toevoegen van bronnen is mislukt.';
            }
        },
        addErrors(errors) {
            for (let key in errors.details) {
                if (!errors.details.hasOwnProperty(key)) continue;

                let fieldName = key;
                let field = this.$validator.fields.find({ name: fieldName });

                if (!field) {
                    fieldName = `0.${key}`;
                    field = this.$validator.fields.find({ name: fieldName });
                }

                if (!field) continue;

                this.$validator.errors.add({
                    id: field.id,
                    field: fieldName,
                    msg: errors.details[key][0]
                });

                field.setFlags({
                    invalid: true,
                    valid: false,
                    validated: true
                });
            }
        }
    }
};
</script>

<style>
.modal-container .modal-body {
    overflow: unset;
}
</style>
